// src/models/Insights/liveActiveUsers.ts
//
// Backs the Fleet Status "Active Now" card and its Active Users dialog. Unlike every other
// Insights metric this is not a Cube query: it reads the SignalR-fed `OrgGroupUserStatusStore`
// (the same 30s user-status pings the Map and User Dashboard render), so the list is live
// rather than `DeviceLatestStatus`'s "last event within N minutes" approximation.

import type { StatusStoreState } from '@/context_providers/group_user_context/OrgGroupUserStatusStore';

export type LiveActiveUser = {
  userAuthId: string;
  /** `user_info.name` from the user's latest ping; `null` when the ping carried none. */
  name: string | null;
  /** The status-store group the user's ping was found under. */
  groupName: string;
};

/** Distinct users currently active across every group, sorted by name. A user can be a member
 * of more than one group's status payload, so users are de-duplicated by auth id (the first
 * group they are found under wins) rather than listed once per group. */
export function listActiveUsers(groupUserStatuses: StatusStoreState): LiveActiveUser[] {
  const activeUsersByAuthId = new Map<string, LiveActiveUser>();

  for (const [groupName, groupStatuses] of Object.entries(groupUserStatuses)) {
    for (const [userAuthId, userStatus] of Object.entries(groupStatuses?.user_statuses ?? {})) {
      if (activeUsersByAuthId.has(userAuthId) || !userStatus?.isActive()) continue;
      activeUsersByAuthId.set(userAuthId, {
        userAuthId,
        name: userStatus.dto?.user_info?.name?.trim() || null,
        groupName,
      });
    }
  }

  return [...activeUsersByAuthId.values()].sort(compareLiveActiveUsersByName);
}

/** Unnamed users sort last so the named rows an admin scans for stay together at the top. */
export function compareLiveActiveUsersByName(a: LiveActiveUser, b: LiveActiveUser): number {
  if (a.name === null || b.name === null) {
    if (a.name === b.name) return 0;
    return a.name === null ? 1 : -1;
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}
